import { apiClient } from '../utils/apiClient.js';
import appInsightsLogger from '../utils/appInsights.js';
/** @typedef {import('../models/types').SendPasswordResetEmailRequestDto} SendPasswordResetEmailRequestDto */
/** @typedef {import('../models/types').ResetPasswordRequestDto} ResetPasswordRequestDto */
/** @typedef {import('../models/types').ChangePasswordRequestDto} ChangePasswordRequestDto */
/** @typedef {import('../models/types').GetAllUserDetailsResponseDto} GetAllUserDetailsResponseDto */
/** @typedef {import('../models/types').MarkLastLoginRequestDto} MarkLastLoginRequestDto */

/**
 * Validate email format
 * @param {string} email 
 * @returns {boolean}
 */
function isValidEmail(email) {
  if (!email || typeof email !== 'string') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
}

/**
 * Validate password format
 * @param {string} password 
 * @returns {boolean}
 */
function isValidPassword(password) {
  return password && typeof password === 'string' && password.length >= 8 && password.length <= 128;
}

/**
 * Validate user ID
 * @param {string|number} userId 
 * @returns {boolean}
 */
function isValidUserId(userId) {
  return userId && (typeof userId === 'string' || typeof userId === 'number');
}

/**
 * Mark last login for a user.
 * @param {MarkLastLoginRequestDto} payload
 * @returns {Promise<any>}
 */
export async function markLastLogin(payload) {
  if (!payload || !isValidUserId(payload.userId)) {
    throw new Error('Invalid user ID for marking last login');
  }

  try {
    appInsightsLogger.trackEvent('User_Mark_Last_Login', {
      component: 'userService'
    });

      return await apiClient.post('/api/User/mark-last-login', payload);
  } catch (error) {
    appInsightsLogger.trackException(error, {
      component: 'userService',
      operation: 'markLastLogin'
    });
    throw error;
  }
}

/**
 * Request password reset email.
 * @param {SendPasswordResetEmailRequestDto} payload
 * @returns {Promise<any>}
 */
export async function requestPasswordReset(payload) {
  if (!payload || !isValidEmail(payload.targetUserEmail)) {
    throw new Error('Invalid email for password reset request');
  }

  try {
    appInsightsLogger.trackEvent('User_Password_Reset_Request', {
      component: 'userService',
      emailDomain: payload.targetUserEmail.split('@')[1] || 'unknown'
    });

    return await apiClient.post('/api/User/password-reset-request', payload);
  } catch (error) {
    appInsightsLogger.trackException(error, {
      component: 'userService',
      operation: 'requestPasswordReset',
      emailDomain: payload.email?.split('@')[1] || 'unknown'
    });
    throw error;
  }
}

/**
 * Validate password reset token.
 * @param {string} token
 * @returns {Promise<any>}
 */
export async function validateResetToken(token) {
  if (!token || typeof token !== 'string' || token.length < 10) {
    throw new Error('Invalid reset token');
  }
  
  try {
    appInsightsLogger.trackEvent('User_Validate_Reset_Token', {
      component: 'userService'
    });
    // Always pass a third argument (headers object)
    return await apiClient.post('/api/User/validate-reset-token', token, {});
  } catch (error) {
    appInsightsLogger.trackException(error, {
      component: 'userService',
      operation: 'validateResetToken'
    });
    throw error;
  }
}

/**
 * Reset user password.
 * @param {ResetPasswordRequestDto} payload
 * @returns {Promise<any>}
 */
export async function resetPassword(payload) {
  if (!payload || !payload.Token || !isValidPassword(payload.NewPassword)) {
    throw new Error('Invalid password reset payload');
  }

  try {
    appInsightsLogger.trackEvent('User_Password_Reset', {
      component: 'userService'
    });

    return await apiClient.post('/api/User/reset-password', payload);
  } catch (error) {
    appInsightsLogger.trackException(error, {
      component: 'userService',
      operation: 'resetPassword'
    });
    throw error;
  }
}

/**
 * Change user password.
 * @param {ChangePasswordRequestDto} payload
 * @returns {Promise<any>}
 */
export async function changePassword(payload) {
  if (!payload || !isValidEmail(payload.Email) || !isValidPassword(payload.NewPassword)) {
    throw new Error('Invalid password change payload');
  }

  try {
    appInsightsLogger.trackEvent('User_Change_Password', {
      component: 'userService'
    });

    return await apiClient.post('/api/User/change-password', payload);
  } catch (error) {
    appInsightsLogger.trackException(error, {
      component: 'userService',
      operation: 'changePassword'
    });
    throw error;
  }
}

/**
 * Change user password for first time user.
 * @param {ChangePasswordRequestDto} payload
 * @returns {Promise<any>}
 */
export async function changePasswordForFirstTimeUser(payload) {
  if (!payload || !isValidEmail(payload.Email) || !isValidPassword(payload.NewPassword)) {
    throw new Error('Invalid password change payload');
  }

  try {
    appInsightsLogger.trackEvent('User_Change_Password_First_Time', {
      component: 'userService'
    });

    const headers = { 'Content-Type': 'application/json' };
    return await apiClient.post('/api/User/change-password-firstTimeUser', payload, {
      headers
    });
  } catch (error) {
    appInsightsLogger.trackException(error, {
      component: 'userService',
      operation: 'changePasswordForFirstTimeUser'
    });
    throw error;
  }
}

/**
 * Get user details by username.
 * @param {string} username
 * @returns {Promise<any>}
 */
export async function getUserDetails(username) {
  try {
    appInsightsLogger.trackEvent('User_Get_Details', {
      component: 'userService',
      username: username
    });

    return await apiClient.get(`/api/User/details?username=${encodeURIComponent(username)}`);
  } catch (error) {
    appInsightsLogger.trackException(error, {
      component: 'userService',
      operation: 'getUserDetails',
      username: username
    });
    throw error;
  }
}

/**
 * Get all user details.
 * @returns {Promise<GetAllUserDetailsResponseDto[]>}
 */
export async function getAllUserDetails() {
  try {
    appInsightsLogger.trackEvent('User_Get_All_Details', {
      component: 'userService'
    });

    return await apiClient.get('/api/User/all-details');
  } catch (error) {
    appInsightsLogger.trackException(error, {
      component: 'userService',
      operation: 'getAllUserDetails'
    });
    throw error;
  }
}